import {
  Check,
  ImageIcon,
  PenLine,
  Receipt,
  Sparkles,
  Stamp,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useDesignsApi } from 'src/api/db/apiDesigns'
import { useQuoteSubItemApi } from 'src/api/db/apiQuoteSubItem'
import { queryKeys } from 'src/api/db/queryKeys'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui'
import { cn } from 'src/shared/components/ui/utils'
import { useLocalization } from 'src/shared/hooks/useLocalization'

import { ArtworkPicker } from './components/ArtworkPicker'
import { DecorationPicker } from './components/DecorationPicker'
import { IllustrationPicker } from './components/IllustrationPicker'
import { useGetDecorationInfo } from 'src/api/server/useGetDecorationInfo'
import { useOrgsApi } from 'src/api/db/apiOrgs'

type OnboardingMode = 'design' | 'quote'

export const DecorationOnboarding = ({
  quoteItem,
  quoteSubItem,
  productImages,
}) => {
  const { t } = useLocalization()
  const queryClient = useQueryClient()
  const { updateQuoteSubItem } = useQuoteSubItemApi()
  const { updateDesign } = useDesignsApi()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { decorations } = useGetDecorationInfo({
    product_id: quoteItem?.product?.id!,
    item_quantity: quoteSubItem.quantity ?? 1,
    item_colors: quoteSubItem.d_colors ?? 1,
  })

  const org_id = quoteItem?.quote?.org_id!

  const { organizationById } = useOrgsApi({
    org_id,
  })
  const companyUrl = organizationById?.data?.company_url

  const [mode, setMode] = useState<OnboardingMode | null>(null)
  const [printCode, setPrintCode] = useState<string | undefined>()
  const [imageUrl, setImageUrl] = useState<string | undefined>()
  const [designUrl, setDesignUrl] = useState<string | undefined>()

  const isQuoteMode = mode === 'quote'
  const isDesignMode = mode === 'design'

  const isDecorationComplete = !!printCode
  const isIllustrationComplete = !!imageUrl
  const isArtworkComplete = !!designUrl

  const fallbackImageUrl =
    productImages?.product_image?.[0]?.image_url ?? null

  const handleStartDesigning = async () => {
    setIsSubmitting(true)
    try {
      await updateQuoteSubItem.mutateAsync({
        id: quoteSubItem.id,
        print_code: printCode,
        vendor: quoteItem.product.source,
      })

      await updateDesign.mutateAsync({
        id: quoteSubItem.design[0].id,
        image_url: isQuoteMode ? fallbackImageUrl : imageUrl,
        design_url: isQuoteMode ? null : (designUrl ?? null),
        x: 600 / 2 - 50,
        y: 600 / 2 - 50,
      })

      queryClient.invalidateQueries({ queryKey: [queryKeys.quoteItemById] })
    } finally {
      setIsSubmitting(false)
    }
  }

  const canStartDesigning = isQuoteMode
    ? isDecorationComplete
    : isDecorationComplete && isIllustrationComplete && isArtworkComplete

  return (
    <Column className="relative w-full min-h-dvh">
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
        <div className="absolute inset-0 bg-[radial-gradient(circle,rgb(0_0_0_/_0.06)_1px,transparent_1px)] bg-[length:18px_18px]" />
        <div className="absolute -left-16 top-0 h-72 w-72 rounded-full bg-violet-300/30 blur-3xl" />
        <div className="absolute -right-12 top-16 h-64 w-64 rounded-full bg-sky-300/25 blur-3xl" />
        <div className="absolute left-1/3 top-40 h-48 w-48 rounded-full bg-fuchsia-200/20 blur-3xl" />
      </div>

      <Column className="relative z-10 mx-auto w-full max-w-3xl gap-5 px-4 py-8">
        <Column className="items-center gap-3 pt-2">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-600 shadow-lg shadow-violet-500/25">
            <Sparkles className="h-5 w-5 text-white" strokeWidth={2} />
          </div>
          <Column className="items-center gap-1">
            <h2>{t("Let's get started!", 'Låt oss komma igång!')}</h2>
            <p className="max-w-md text-center text-sm text-muted-foreground">
              {t(
                'How do you want to set up this decoration?',
                'Hur vill du sätta upp den här dekorationen?'
              )}
            </p>
          </Column>
        </Column>

        <Row className="gap-3">
          <ModeCard
            selected={isDesignMode}
            onClick={() => setMode('design')}
            icon={<PenLine className="h-5 w-5" />}
            iconGradient="from-sky-400 to-blue-500"
            title={t('Design with artwork', 'Designa med artwork')}
            description={t(
              'Place a logo on a product image and fine-tune the mockup.',
              'Placera en logotyp på en produktbild och finjustera mockupen.'
            )}
          />
          <ModeCard
            selected={isQuoteMode}
            onClick={() => setMode('quote')}
            icon={<Receipt className="h-5 w-5" />}
            iconGradient="from-violet-400 to-indigo-500"
            title={t('Quote only', 'Endast offert')}
            description={t(
              'Add decoration and pricing now — artwork can come later.',
              'Lägg till dekoration och pris nu — artwork kan komma senare.'
            )}
          />
        </Row>

        {mode && (
          <Column className="gap-4">
            <OnboardingCard
              icon={<Stamp className="h-3.5 w-3.5" />}
              title={t('Decoration type', 'Tryckteknik')}
              description={t(
                'Choose the print technique for your design.',
                'Välj tryckteknik för din design.'
              )}
            >
              <DecorationPicker
                withTitle={false}
                withCard={false}
                fullHeight
                decorations={decorations}
                quoteSubItem={quoteSubItem}
                selectedPrintCode={printCode}
                setPrintCode={(val) => setPrintCode(val.print_code)}
              />
            </OnboardingCard>

            {isDesignMode && (
              <>
                <OnboardingCard
                  icon={<ImageIcon className="h-3.5 w-3.5" />}
                  title={t('Illustration', 'Illustration')}
                  description={t(
                    'Choose which product image to place your design onto.',
                    'Välj vilken produktbild du vill placera din design på.'
                  )}
                >
                  <IllustrationPicker
                    withTitle={false}
                    withCard={false}
                    large
                    productImages={productImages}
                    selectedProductImage={imageUrl}
                    onSelectProductImage={setImageUrl}
                  />
                </OnboardingCard>

                <OnboardingCard
                  icon={<PenLine className="h-3.5 w-3.5" />}
                  title={t('Artwork', 'Artwork')}
                  description={t(
                    'Upload or select your logo or artwork.',
                    'Ladda upp eller välj din logotyp eller artwork.'
                  )}
                >
                  <ArtworkPicker
                    withTitle={false}
                    withCard={false}
                    selectedDesignUrl={designUrl}
                    onSelectDesignUrl={setDesignUrl}
                    onColorCountChange={() => {}}
                    companyUrl={companyUrl}
                    org_id={org_id}
                  />
                </OnboardingCard>
              </>
            )}

            {isQuoteMode && (
              <Row className="items-start gap-3 rounded-2xl border border-dashed border-blue-200/80 bg-gradient-to-br from-blue-50/60 via-sky-50/40 to-white px-4 py-3.5">
                <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-sky-100 to-blue-100 text-blue-600 shadow-sm">
                  <ImageIcon className="h-4 w-4" />
                </div>
                <Column className="gap-0.5">
                  <p className="text-sm font-medium text-gray-900">
                    {t('Artwork skipped', 'Artwork hoppas över')}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {t(
                      'Illustration and logo are optional for quoting. You can add them anytime from the editor.',
                      'Illustration och logotyp är valfria för offerten. Du kan lägga till dem när som helst i editorn.'
                    )}
                  </p>
                </Column>
              </Row>
            )}
          </Column>
        )}

        {mode && (
          <Button
            className="w-full"
            variant="primary"
            size="lg"
            radius="xl"
            onClick={handleStartDesigning}
            disabled={!canStartDesigning || isSubmitting}
            isLoading={isSubmitting}
          >
            {isQuoteMode
              ? t('Continue to quote', 'Fortsätt till offert')
              : t('Start designing', 'Börja designa')}
          </Button>
        )}
      </Column>
    </Column>
  )
}

const ModeCard = ({
  selected,
  onClick,
  icon,
  iconGradient,
  title,
  description,
}: {
  selected: boolean
  onClick: () => void
  icon: ReactNode
  iconGradient: string
  title: string
  description: string
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'group relative flex-1 overflow-hidden rounded-2xl border-2 p-5 text-left transition-all duration-200',
      selected
        ? 'border-blue-500 bg-gradient-to-br from-blue-50/80 via-blue-100/70 to-sky-100/80 shadow-md ring-2 ring-blue-500/25'
        : 'border-gray-200/80 bg-white/80 shadow-sm backdrop-blur-sm hover:border-gray-300 hover:shadow-md'
    )}
  >
    <Column className="relative z-10 gap-3.5">
      <Row className="items-center justify-between">
        <div
          className={cn(
            'flex h-11 w-11 items-center justify-center rounded-2xl text-white shadow-lg transition-transform duration-200 group-hover:scale-105',
            selected
              ? 'bg-blue-500 shadow-blue-500/30'
              : `bg-gradient-to-br ${iconGradient} shadow-black/10`
          )}
        >
          {icon}
        </div>
        <div
          className={cn(
            'flex h-5 w-5 items-center justify-center rounded-full border-2 transition-colors',
            selected
              ? 'border-blue-500 bg-blue-500 text-white'
              : 'border-gray-300 bg-white'
          )}
        >
          {selected && <Check className="h-3 w-3" strokeWidth={3} />}
        </div>
      </Row>
      <Column className="gap-1">
        <p className="text-sm font-semibold text-gray-900">{title}</p>
        <p className="text-xs leading-relaxed text-muted-foreground">
          {description}
        </p>
      </Column>
    </Column>
  </button>
)

const OnboardingCard = ({
  icon,
  title,
  description,
  children,
}: {
  icon: ReactNode
  title: string
  description: string
  children: ReactNode
}) => (
  <div className="overflow-hidden rounded-2xl border border-white/70 bg-white/80 p-4 shadow-[0_16px_40px_-12px_rgba(99,102,241,0.12)] backdrop-blur-md">
    <Row className="mb-3 items-start gap-2.5">
      <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-violet-500 to-indigo-600 text-white shadow-sm">
        {icon}
      </div>
      <Column className="min-w-0 gap-0.5">
        <p className="text-sm font-semibold text-gray-900">{title}</p>
        <p className="text-sm text-muted-foreground">{description}</p>
      </Column>
    </Row>
    {children}
  </div>
)
